import { useState } from "react";
import { supabase } from "../lib/supabaseClient";

interface NewSuggestionModalProps {
  tripId: string;
  userId: string;
  suggestion?: {
    id: string;
    title: string;
    description?: string | null;
    category?: string | null;
    user_id: string;
    created_at: string | null;
  };
  onClose: (updated?: any) => void;
}

export default function NewSuggestionModal({
  tripId,
  userId,
  suggestion,
  onClose,
}: NewSuggestionModalProps) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [title, setTitle] = useState(suggestion?.title ?? "");
  const [description, setDescription] = useState(suggestion?.description ?? "");
  const [category, setCategory] = useState(suggestion?.category ?? "Aktivität");

  const handleSubmit = async () => {
    if (!title.trim()) {
      setError("Ein Titel ist erforderlich.");
      return;
    }

    setSaving(true);

    if (suggestion) {
      // ✏️ UPDATE bestehender Vorschlag
      const { error } = await supabase
        .from("trip_suggestions")
        .update({
          title: title.trim(),
          description: description.trim(),
          category,
        })
        .eq("id", suggestion.id);

      if (error) {
        setError("Fehler beim Aktualisieren.");
      } else {
        onClose({
          ...suggestion,
          title: title.trim(),
          description: description.trim(),
          category,
        });
      }
    } else {
      // ➕ NEUER Vorschlag
      const { data, error } = await supabase
        .from("trip_suggestions")
        .insert({
          trip_id: tripId,
          user_id: userId,
          title: title.trim(),
          description: description.trim(),
          category,
        })
        .select()
        .single();

      if (error) {
        setError("Fehler beim Speichern.");
      } else {
        onClose({
          id: data.id,
          title: data.title,
          description: data.description,
          category: data.category,
          user_id: data.user_id,
          created_at: data.created_at,
        });
      }
    }

    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
      <div className="bg-[#161b27] rounded-2xl border border-[#2a3347] p-6 w-full max-w-md relative">
        <button
          onClick={() => onClose()}
          className="absolute top-3 right-4 text-slate-600 hover:text-slate-300 text-xl transition-colors"
          aria-label="Modal schließen"
        >
          &times;
        </button>

        <h2 className="text-sm font-semibold text-slate-100 mb-5">
          {suggestion ? "✏️ Vorschlag bearbeiten" : "💡 Neuen Vorschlag einreichen"}
        </h2>

        <input
          type="text"
          placeholder="Titel"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="w-full bg-[#1e2639] border border-[#2a3347] rounded-xl text-slate-100 px-3 py-2 text-sm mb-3 focus:outline-none focus:border-sky-500/50 placeholder:text-slate-600"
        />
        <textarea
          placeholder="Beschreibung (optional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="w-full bg-[#1e2639] border border-[#2a3347] rounded-xl text-slate-100 px-3 py-2 text-sm mb-3 focus:outline-none focus:border-sky-500/50 placeholder:text-slate-600"
          rows={3}
        />

        <select
          className="w-full bg-[#1e2639] border border-[#2a3347] rounded-xl text-slate-100 px-3 py-2 text-sm mb-4 focus:outline-none focus:border-sky-500/50"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
        >
          <option value="Aktivität">Aktivität</option>
          <option value="Verpflegung">Verpflegung</option>
          <option value="Organisation">Organisation</option>
          <option value="Sonstiges">Sonstiges</option>
        </select>

        {error && <p className="text-xs text-red-400 mb-3">{error}</p>}

        <div className="flex justify-end gap-2">
          <button
            onClick={() => onClose()}
            className="text-xs px-3 py-1.5 rounded-full bg-[#1e2639] text-slate-400 hover:text-slate-200 transition-colors"
          >
            Abbrechen
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="text-xs px-3 py-1.5 rounded-full bg-sky-600 text-white hover:bg-sky-500 transition-colors"
          >
            {saving ? "Speichern…" : "Speichern"}
          </button>
        </div>
      </div>
    </div>
  );
}
